import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {FormGroup} from '@angular/forms';

import {Translatable, TranslationService} from '@ngmedax/translation';

import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';


// hack to inject decorator declarations. must occur before class declaration!
export interface PatientActionButtonsComponent extends Translatable {}

/**
 * Feature configuration for patient actions.
 */
export interface PatientFeature {
  patient: {
    app: {
      mail: boolean;
      link: {
        send: boolean;
        copy: boolean;
        open: boolean;
      };
    };
    anonymous: boolean;
  };
}

/**
 * Component for patient action buttons (save, anonymous toggle, link actions).
 * Encapsulates the footer button bar of the patient CRUD form.
 */
@Component({
  selector: 'app-patient-action-buttons',
  templateUrl: './patient-action-buttons.component.html',
  styleUrls: ['./patient-action-buttons.component.css']
})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class PatientActionButtonsComponent {
  /**
   * The patient form group.
   */
  @Input() patientForm: FormGroup;

  /**
   * The custom fields form group.
   */
  @Input() customFieldsForm: FormGroup;

  /**
   * Array of selected questionnaire IDs.
   */
  @Input() selectedQuestionnaires: string[] = [];

  /**
   * Whether the form has unsaved changes.
   */
  @Input() dirty: boolean = true;

  /**
   * Feature configuration.
   */
  @Input() feature: PatientFeature = {
    patient: {app: {mail: false, link: {send: false, copy: false, open: false}}, anonymous: false}
  };

  /**
   * Whether the component is in overlay mode.
   */
  @Input() overlayMode: boolean = false;

  /**
   * Whether to hide the form (assign mode).
   */
  @Input() hideForm: boolean = false;

  /**
   * Whether survey mail is configured.
   */
  @Input() isSurveyMailConfigured: boolean = false;

  /**
   * Whether user toggle is disabled.
   */
  @Input() disableUserToggle: boolean = false;

  /**
   * Whether to show close window button.
   */
  @Input() showCloseWinBtn: boolean = false;

  /**
   * Emitted when save button is clicked.
   */
  @Output() save = new EventEmitter<void>();

  /**
   * Emitted when anonymous toggle is clicked.
   */
  @Output() toggleAnonymous = new EventEmitter<void>();

  /**
   * Emitted when pseudonymous toggle is clicked.
   */
  @Output() togglePseudonymous = new EventEmitter<void>();

  /**
   * Emitted when create jump-in code button is clicked.
   */
  @Output() createJumpInCode = new EventEmitter<void>();

  /**
   * Emitted when fill start button is clicked.
   */
  @Output() fillStart = new EventEmitter<void>();

  /**
   * Emitted when copy link button is clicked.
   */
  @Output() copyLink = new EventEmitter<void>();

  /**
   * Emitted when send mail button is clicked.
   */
  @Output() sendMail = new EventEmitter<void>();

  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private translationService: TranslationService
  ) {
  }

  /**
   * Gets the patient UID from the form.
   */
  public get patientUid(): string | null {
    return this.patientForm?.get('uid')?.value || null;
  }

  /**
   * Checks if anonymous mode is active.
   */
  public get isAnonymous(): boolean {
    return !!this.patientForm?.get('anonymous')?.value;
  }

  /**
   * Checks if pseudonymous mode is active.
   */
  public get isPseudonymous(): boolean {
    return !!this.patientForm?.get('pseudonymous')?.value;
  }

  /**
   * Checks if save button should be disabled.
   */
  public get isSaveDisabled(): boolean {
    const formInvalid = !this.hideForm && !this.patientForm?.valid && !this.isAnonymous && !this.isPseudonymous;
    const customInvalid = this.customFieldsForm && this.customFieldsForm.invalid;
    return formInvalid || customInvalid;
  }

  /**
   * Checks if jump-in code button should be disabled.
   */
  public get isJumpInCodeDisabled(): boolean {
    return !(this.selectedQuestionnaires.length && !this.dirty);
  }

  /**
   * Checks if link actions should be disabled.
   */
  public get isLinkActionsDisabled(): boolean {
    return !this.patientUid || !this.selectedQuestionnaires.length;
  }

  /**
   * Checks if anonymous toggle should be shown.
   */
  public get showAnonymousToggle(): boolean {
    return !this.patientUid && this.feature.patient.anonymous && !this.disableUserToggle;
  }

  /**
   * Checks if link separator should be shown.
   */
  public get showLinkSeparator(): boolean {
    return this.feature.patient.app.link.open ||
           this.feature.patient.app.link.send ||
           this.feature.patient.app.link.copy;
  }

  /**
   * Handles save button click.
   */
  public onSave(): void {
    this.save.emit();
  }

  /**
   * Handles anonymous toggle click.
   */
  public onToggleAnonymous(): void {
    this.toggleAnonymous.emit();
  }

  /**
   * Handles pseudonymous toggle click.
   */
  public onTogglePseudonymous(): void {
    this.togglePseudonymous.emit();
  }

  /**
   * Handles create jump-in code button click.
   */
  public onCreateJumpInCode(): void {
    this.createJumpInCode.emit();
  }

  /**
   * Handles fill start button click.
   */
  public onFillStart(): void {
    this.fillStart.emit();
  }

  /**
   * Handles copy link button click.
   */
  public onCopyLink(): void {
    this.copyLink.emit();
  }

  /**
   * Handles send mail button click.
   */
  public onSendMail(): void {
    this.sendMail.emit();
  }
}

